import React from 'react';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { TertiaryButton } from '@grnhse/seedling/lib/birch/components/buttons';
import {
  Body,
  SecondaryBody,
  InlineMarkdown,
} from '@grnhse/seedling/lib/birch/typography';
import { useFormContext } from '@grnhse/seedling/lib/birch/components/forms/form';
import { withLocalePrefix } from 'shared/utils/translation';
import PopoverButton from 'talent_matching/components/filters/popover_button';
import { useRediscoveryTabContext } from 'talent_matching/contexts/rediscovery_tab_context';
import RejectionReasonsDropdown from './rejection_reasons_dropdown';
import ExcludeTagsDropdown from './exclude_tags_dropdown';
import ExcludeHiredCheckbox from './exclude_hired_checkbox';
import ExcludeStrongNoScoresCheckbox from './exclude_strong_no_scores_checkbox';
import ExcludeRejectedCheckbox from './exclude_rejected_checkbox';
import ExcludeCurrentJobPeopleCheckbox from './exclude_current_job_people_checkbox';
import ExcludeStrongNoAttributesCheckbox from './exclude_strong_no_attributes_checkbox';

const t = withLocalePrefix('talent_matching.rediscovery.filter_row');

const ExclusionFiltersGroup = () => {
  const { apiClient } = useRediscoveryTabContext();
  const { watch, setValue } = useFormContext();
  const [
    excludeRejectionReasons,
    withoutCandidateTags,
    excludeHired,
    excludeRejected,
    excludeCurrentJobPeople,
    excludeStrongNoScores,
    excludeStrongNoAttributes,
  ] = watch([
    'exclude_rejection_reasons',
    'without_candidate_tags',
    'exclude_hired',
    'exclude_rejected',
    'exclude_current_job_people',
    'no_strong_no_scores',
    'exclude_strong_no_attributes',
  ]);

  const hasExcludeRejectionReasons =
    Array.isArray(excludeRejectionReasons) &&
    excludeRejectionReasons.length > 0;
  const hasWithoutCandidateTags =
    Array.isArray(withoutCandidateTags) && withoutCandidateTags.length > 0;

  const appliedCount = [
    hasExcludeRejectionReasons,
    hasWithoutCandidateTags,
    excludeHired,
    excludeRejected,
    excludeCurrentJobPeople,
    excludeStrongNoScores,
    excludeStrongNoAttributes,
  ].filter(Boolean).length;

  const clear = () => {
    setValue('exclude_rejection_reasons', [], { shouldDirty: true });
    setValue('without_candidate_tags', [], { shouldDirty: true });
    setValue('exclude_hired', false, { shouldDirty: true });
    setValue('exclude_rejected', false, { shouldDirty: true });
    setValue('exclude_current_job_people', false, { shouldDirty: true });
    setValue('no_strong_no_scores', false, { shouldDirty: true });
    setValue('exclude_strong_no_attributes', false, { shouldDirty: true });
  };

  const buttonLabel = `${t('exclusions.button')}${
    appliedCount ? ` (${appliedCount})` : ''
  }`;

  return (
    <PopoverButton
      buttonLabel={buttonLabel}
      title={t('exclusions.title')}
      width="640px"
      contentStyles={{ paddingTop: 0 }}
    >
      <Stack spacing={3}>
        <Body>
          <InlineMarkdown text={t('exclusions.description')} />
        </Body>
        <RejectionReasonsDropdown
          fetchRejectionReasons={apiClient.fetchRejectionReasons}
          type="exclude"
          helperText={t('exclusions.exclude_rejection_reasons_helper_text')}
        />

        <ExcludeTagsDropdown />

        <Stack spacing={2}>
          <SecondaryBody medium>
            {t('exclusions.scorecard_exclusions_header')}
          </SecondaryBody>
          <Stack spacing={1}>
            <ExcludeStrongNoScoresCheckbox />
            <ExcludeStrongNoAttributesCheckbox />
          </Stack>
        </Stack>

        <Stack spacing={2}>
          <SecondaryBody medium>
            {t('exclusions.global_exclusions_header')}
          </SecondaryBody>
          <Stack spacing={1}>
            <ExcludeHiredCheckbox />
            <ExcludeRejectedCheckbox />
            <ExcludeCurrentJobPeopleCheckbox />
          </Stack>
        </Stack>

        <Stack sx={{ alignSelf: 'flex-start' }}>
          <TertiaryButton onClick={clear} size="md">
            {t('clear')}
          </TertiaryButton>
        </Stack>
      </Stack>
    </PopoverButton>
  );
};

export default ExclusionFiltersGroup;
